import Navbar from "@/components/feature/Navbar";
import HeroSection from "./components/HeroSection";
import CategoryGrid from "./components/CategoryGrid";
import ArticleCard from "./components/ArticleCard";
import FAQSection from "./components/FAQSection";
import Footer from "./components/Footer";
import SEOHead from "@/components/base/SEOHead";
import { articleList } from "@/mocks/article-data";

function getSiteUrl(): string {
  try {
    return import.meta.env.VITE_SITE_URL || "https://superbuy.study";
  } catch {
    return "https://superbuy.study";
  }
}

const homeJsonLd = (siteUrl: string) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Superbuy Study",
  url: siteUrl,
  description: "Independent educational resource for Superbuy spreadsheet guides, product lists, and buying tips for international shoppers.",
  potentialAction: {
    "@type": "SearchAction",
    target: `${siteUrl}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

const homeOrgJsonLd = (siteUrl: string) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Superbuy Study",
  url: siteUrl,
  description: "Educational resource helping international buyers navigate Superbuy with curated spreadsheets, guides, and safety information.",
  sameAs: [
    "https://kaspreadsheet.com/",
  ],
});

const HOME_OG_IMAGE = "https://readdy.ai/api/search-image?query=Elegant%20fashion%20product%20flat%20lay%20arrangement%20on%20warm%20sand%20colored%20textured%20surface%20assorted%20premium%20sneakers%20hoodies%20t-shirts%20bags%20and%20accessories%20beautifully%20styled%20with%20soft%20natural%20window%20light%20casting%20gentle%20shadows%20minimalist%20editorial%20composition%20modern%20lifestyle%20aesthetic%20cream%20and%20beige%20neutral%20color%20palette%20professional%20e-commerce%20photography%20harmonious%20organic%20layout&width=1200&height=630&seq=og-home-v1&orientation=landscape";

export default function Home() {
  const siteUrl = getSiteUrl();

  return (
    <>
      <SEOHead
        title="Best Superbuy Spreadsheet 2026 | Guides, Lists & Buying Tips"
        description="Discover the best Superbuy spreadsheets for 2026. Comprehensive buying guides, shipping tips, safety advice, and updated product lists for international buyers. Find sneakers, hoodies, jackets, and more with our curated Superbuy spreadsheet."
        keywords="superbuy spreadsheet, best superbuy spreadsheet 2026, superbuy agent spreadsheet, spreadsheet for superbuy, superbuy buying guide, superbuy product list"
        canonicalPath="/"
        ogImage={HOME_OG_IMAGE}
        jsonLd={[homeJsonLd(siteUrl), homeOrgJsonLd(siteUrl)]}
      />
      <Navbar />
      <main>
        <HeroSection />
        <CategoryGrid />

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wide uppercase mb-3">
                SEO Guides & Articles
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-3">
                Master Superbuy Shopping with Our Guides
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                In-depth articles covering everything from beginner basics to advanced shopping strategies — all optimized to help you shop smarter.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {articleList.map((article) => (
                <ArticleCard
                  key={article.slug}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  readTime={article.readTime}
                  category={article.category}
                  imageUrl={article.imageUrl}
                />
              ))}
            </div>
          </div>
        </section>

        <FAQSection />
      </main>
      <Footer />
    </>
  );
}